import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Avatar } from '@mui/material';
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';

const Chat = () => {
    const [chats, setChats] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [openNewChat, setOpenNewChat] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [groupName, setGroupName] = useState('');
    const [isGroupChat, setIsGroupChat] = useState(false);

    useEffect(() => {
        // Fetch all users
        const fetchUsers = async () => {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const usersList = usersSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setUsers(usersList);
        };

        fetchUsers();

        // Listen for chats where the current user is a participant
        const q = query(
            collection(db, 'chats'),
            where('participants', 'array-contains', auth.currentUser?.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chatsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setChats(chatsList);
        });

        return () => unsubscribe();
    }, []);

    const handleCreateChat = async () => {
        if (selectedUsers.length === 0) return;

        try {
            const participants = [...selectedUsers.map(user => user.id), auth.currentUser.uid];
            const chatData = {
                participants,
                createdAt: new Date(),
                isGroup: isGroupChat,
                groupName: isGroupChat ? groupName : null,
                lastMessage: null,
                lastMessageTime: null
            };

            await addDoc(collection(db, 'chats'), chatData);
            setOpenNewChat(false);
            setSelectedUsers([]);
            setGroupName('');
            setIsGroupChat(false);
        } catch (error) {
            console.error('Error creating chat:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!selectedChat || !newMessage.trim()) return;

        try {
            const messageData = {
                text: newMessage,
                senderId: auth.currentUser.uid,
                senderName: auth.currentUser.displayName,
                timestamp: new Date(),
                chatId: selectedChat.id
            };

            // Add message to messages collection
            await addDoc(collection(db, 'messages'), messageData);

            // Update chat's last message
            await updateDoc(doc(db, 'chats', selectedChat.id), {
                lastMessage: newMessage,
                lastMessageTime: new Date()
            });

            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
        }
    };

    const handleUserSelect = (user) => {
        if (selectedUsers.some(u => u.id === user.id)) {
            setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
    };

    return (
        <Box sx={{ height: '100%', p: 2 }}>
            <Grid container spacing={2} sx={{ height: '100%' }}>
                <Grid item xs={12} md={4}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        mb: 2 
                    }}>
                        <Typography variant="h6">Chats</Typography>
                        <Box>
                            <IconButton 
                                onClick={() => {
                                    setIsGroupChat(false);
                                    setOpenNewChat(true);
                                }}
                                color="primary"
                            >
                                <AddIcon />
                            </IconButton>
                            <IconButton 
                                onClick={() => {
                                    setIsGroupChat(true);
                                    setOpenNewChat(true);
                                }}
                                color="primary"
                            >
                                <GroupIcon />
                            </IconButton>
                        </Box>
                    </Box>
                    <ChatList 
                        chats={chats} 
                        users={users} 
                        onSelectChat={setSelectedChat} 
                        selectedChat={selectedChat}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    {selectedChat ? (
                        <ChatWindow
                            messages={[]} // You'll need to fetch messages for the selected chat
                            newMessage={newMessage}
                            setNewMessage={setNewMessage}
                            onSendMessage={handleSendMessage}
                            selectedChat={selectedChat}
                            users={users}
                            onSelectChat={setSelectedChat}
                        />
                    ) : (
                        <Box sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            <Typography variant="h6" color="text.secondary">
                                Select a chat to start messaging
                            </Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* New Chat Dialog */}
            <Dialog open={openNewChat} onClose={() => setOpenNewChat(false)}>
                <DialogTitle>
                    {isGroupChat ? 'Create Group Chat' : 'New Chat'}
                </DialogTitle>
                <DialogContent>
                    {isGroupChat && (
                        <TextField
                            fullWidth
                            label="Group Name"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    )}
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        mb: 2 
                    }}>
                        {selectedUsers.map(user => (
                            <Chip
                                key={user.id}
                                avatar={<Avatar>{user.displayName?.charAt(0)}</Avatar>}
                                label={user.displayName}
                                onDelete={() => handleUserSelect(user)}
                            />
                        ))}
                    </Box>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        {users
                            .filter(user => user.id !== auth.currentUser?.uid)
                            .map(user => (
                                <Box
                                    key={user.id}
                                    onClick={() => handleUserSelect(user)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 1,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <Avatar sx={{ mr: 2 }}>
                                        {user.displayName?.charAt(0)}
                                    </Avatar>
                                    <Typography>{user.displayName}</Typography>
                                </Box>
                            ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenNewChat(false)}>Cancel</Button>
                    <Button 
                        onClick={handleCreateChat} 
                        variant="contained"
                        disabled={selectedUsers.length === 0 || (isGroupChat && !groupName)}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Chat; 